// src/components/StationEntry.js
// NOTE: this component serves every city (nyc/paris/london/milan/berlin/madrid), not just
// august — city==='august' below is one branch among several, not the whole file. See
// DayRitualCore.js for why the august branch renders through a shared component now instead of
// its own markup (architecture investigation: Today and this screen's august branch had drifted
// into two visually different "day detail" experiences for the same underlying concept).
import React, { useState, useRef, useEffect } from 'react'
import { Star } from 'lucide-react'
import Header from './ui/Header'
import PrimaryButton from './ui/PrimaryButton'
import * as content from '../data/content'
import JourneyMap from './JourneyMap'
import DayRitualCore from './DayRitualCore'
// Reuses Today's day-strip classes (.aug-today-strip/-days/-dot/-see-all) directly rather than
// duplicating that CSS here — same visual treatment, one source of truth.
import './AugustToday.css'
import AugustTabBar from './AugustTabBar'
import PullQuoteCard from './PullQuoteCard'

const CHECK_PATH = 'M5 13l4 4 10-11'

export default function StationEntry({ city, session, theme, toggleTheme, nextStation = 1, onBegin, onBack, onBackToLanding, onShowLines, cityProgress, skipMap = false, isFavorited = false, onToggleFavorite, activeTab, onChangeTab, augustTheme, onAugustThemeChange }) {
  const cityData    = content.CITIES[city]
  const days        = (content.DAYS || {})[city]
  const stationData = days?.[nextStation - 1]
  const primary     = city === 'berlin' ? '#b89800' : cityData?.primaryColor
  const unit        = cityData?.unitLabel || 'Station'
  const completedCount = cityProgress || 0

  const [showMap, setShowMap] = useState(!skipMap)
  const [exiting, setExiting] = useState(false)

  // Keep the current day centered in the scrollable strip — same mechanism as AugustToday.js.
  const daysRef = useRef(null)
  const currentDotRef = useRef(null)
  useEffect(() => {
    const container = daysRef.current
    const dot = currentDotRef.current
    if (!container || !dot) return
    const target = dot.offsetLeft - container.clientWidth / 2 + dot.clientWidth / 2
    container.scrollTo({ left: Math.max(0, target), behavior: 'auto' })
  }, [nextStation])

  const handleStationPress = (num) => {
    if (num !== nextStation) return
    setExiting(true)
    setTimeout(() => setShowMap(false), 280)
  }

  const handleBegin = () => {
    const el = document.querySelector('.station-entry-wrap')
    if (el) { el.style.opacity = '0'; el.style.transform = 'translateY(12px)' }
    setTimeout(onBegin, 280)
  }

  // ── MAP VIEW ──
  if (showMap) {
    return (
      <div
        className="jm-prejournal-overlay"
        data-city={city}
        style={{
          opacity: exiting ? 0 : 1,
          transform: exiting ? 'translateY(12px)' : 'translateY(0)',
          transition: 'all 0.28s ease',
        }}
      >
        <Header back={{ label: 'All Lines', onClick: onBack }} />

        <JourneyMap
            city={city}
            cityProgress={cityProgress || (nextStation - 1)}
            mode="prejournal"
            onStationPress={handleStationPress}
            style={{ flex: 1 }}
          />

        <div style={{ padding: '0 var(--screen-padding-x) var(--space-md)' }}>
          <PrimaryButton
            style={{ background: primary }}
            onClick={() => handleStationPress(nextStation)}
          >
            <span>Begin {unit} {nextStation}</span>
            <span>→</span>
          </PrimaryButton>
        </div>
      </div>
    )
  }

  // ── STATION DETAIL VIEW ──
  // august renders through DayRitualCore (the canonical day-detail block, shared with
  // AugustToday.js) instead of its own Header/title/divider markup below — that markup is now
  // reachable only for the other cities. Favorite/share icons are dropped for august
  // specifically: AugustTracklist.js already has its own star/share per row, and PageHeader (the
  // header DayRitualCore renders) has no action slot for extra buttons, so there's no shared
  // place left to put them without extending a component every other PageHeader screen uses too.
  return (
    <div className="station-entry-page" data-city={city}>
      {city !== 'august' && (
        <Header
          back={{ label: 'Your Line', onClick: onBack }}
          action={onToggleFavorite && (
            <div className="station-entry-actions">
              <button
                type="button"
                className={`station-entry-favorite${isFavorited ? ' active' : ''}`}
                aria-label={isFavorited ? 'Unfavorite track' : 'Favorite track'}
                onClick={() => onToggleFavorite(nextStation)}
              >
                <Star size={20} strokeWidth={1.75} fill={isFavorited ? 'currentColor' : 'none'} />
              </button>
            </div>
          )}
        />
      )}
      <div className="station-entry-wrap station-entry-animate">

        {city === 'august' ? (
          <DayRitualCore
            dayNumber={nextStation}
            streak={completedCount}
            trackName={stationData?.stationName || `Track ${nextStation}`}
            ritualText={stationData?.short || stationData?.full || "Today's journey invites you to slow down and notice what's already here."}
            quote={stationData?.quote}
            descriptor="The Ritual"
            ctaLabel={nextStation <= (cityProgress || 0) ? 'View Entry' : 'Write 10 Gratitudes'}
            onCta={handleBegin}
            showBack onBack={onBack}
            augustTheme={augustTheme} onAugustThemeChange={onAugustThemeChange}
          />
        ) : (
          <>
            <div className="station-entry-city-row">
              <div className="station-entry-city-dot" style={{ background: cityData?.primaryColor }} />
              <span className="station-entry-city-label">{cityData?.name} Line</span>
            </div>

            <p className="station-entry-num">{unit} {nextStation}</p>
            <h2 className="station-entry-name">{stationData?.title || cityData?.name}</h2>

            {stationData?.practice && (
              <p className="station-entry-practice-type">{stationData.practice}</p>
            )}

            <div className="station-entry-divider" />

            <p className="station-entry-intro">
              {stationData?.full || stationData?.short || `Today's journey invites you to slow down and notice what's already here.`}
            </p>

            {stationData?.quote && <PullQuoteCard text={stationData.quote} />}

            <PrimaryButton
              style={{ background: cityData?.primaryColor }}
              onClick={handleBegin}
            >
              <span>Write 10 Gratitudes</span>
              <span>→</span>
            </PrimaryButton>
          </>
        )}

        {/* Day-pills + "See all days", matching Today's bottom section exactly (same classes,
            same day-status logic). onShowLines lands on the Journey tab (one tap from the
            actual Tracklist via its own "View tracklist" link) rather than jumping straight to
            Tracklist the way Today's own "See all days" does — StationEntry only receives
            onShowLines, not the dedicated onSeeAllDays App.js wires for Today specifically. */}
        {city === 'august' && (
          <>
            <button className="aug-today-see-all show" onClick={onShowLines}>
              See all days
            </button>

            <div className="aug-today-strip show">
              <div className="aug-today-strip-label">Your journey</div>
              <div className="aug-today-days" ref={daysRef}>
                {Array.from({ length: 28 }, (_, i) => {
                  const status = i < completedCount ? 'done' : i === completedCount ? 'current' : 'next'
                  return (
                    <div
                      key={i}
                      ref={status === 'current' ? currentDotRef : null}
                      className={`aug-today-dot ${status} in`}
                    >
                      {status === 'done'
                        ? <svg viewBox="0 0 24 24"><path d={CHECK_PATH} /></svg>
                        : i + 1}
                    </div>
                  )
                })}
              </div>
            </div>
          </>
        )}

      </div>
      {city === 'august' && <AugustTabBar activeTab={activeTab} onChange={onChangeTab} />}
    </div>
  )
}
